import { useUsage } from "@/features/usage/hooks/useUsage";
import { reportLimitError } from "@/features/usage/limitErrors";
import { useState } from "react";
import { streamAgentCommand } from "../api/playground";
import { Examples } from "./Examples";
import { FIELD, HELP, LABEL, RUN_BUTTON } from "./styles";

const EXAMPLES = [
  "grep the corpus for reported p-values below 0.001",
  "find 5 recent papers on GLP-1 and weight loss",
  "search FDA documents about semaglutide labels",
  "which approved drugs target GLP1R?",
  "show the metadata for PMC7616921",
];

export const ConsoleControls = ({
  signedIn,
  onRequireAuth,
  onAgentStart,
  onAgentToken,
  onAgentEnd,
  getHistory,
}: {
  signedIn: boolean;
  onRequireAuth: () => boolean;
  onAgentStart: () => void;
  onAgentToken: (token: string) => void;
  onAgentEnd: () => void;
  getHistory: () => string[];
}) => {
  const [prompt, setPrompt] = useState("");
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { refetchUsage } = useUsage();

  const generate = async (request: string) => {
    const trimmed = request.trim();
    if (!trimmed || generating) return;
    if (!onRequireAuth()) return;

    setGenerating(true);
    setError(null);
    onAgentStart();
    try {
      await streamAgentCommand(trimmed, getHistory(), onAgentToken);
    } catch (e) {
      const limitMessage = reportLimitError(e, "playground", { refetchUsage });
      setError(limitMessage ?? (e instanceof Error ? e.message : "The agent request failed"));
    } finally {
      onAgentEnd();
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <label
          htmlFor="playground-console-prompt"
          className={LABEL}
        >
          Ask an agent to write the command
        </label>
        <textarea
          id="playground-console-prompt"
          value={prompt}
          rows={3}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void generate(prompt);
            }
          }}
          className={`${FIELD} mt-1 resize-none`}
        />
        <p className={HELP}>
          Claude Sonnet turns your request into a{" "}
          <code className="font-mono text-3xs">paperclip</code> command and
          types it into the console. Review it, then press Enter to run.
        </p>
      </div>

      <div className="flex items-baseline gap-4">
        <button
          type="button"
          onClick={() => void generate(prompt)}
          disabled={generating || (signedIn && !prompt.trim())}
          className={RUN_BUTTON}
        >
          {generating ? "Writing…" : signedIn ? "Write the command" : "Sign in to use the console"}
        </button>
        {error && <p className="text-sm text-red-700" role="alert">{error}</p>}
      </div>

      <Examples
        items={EXAMPLES.map((value) => ({ value }))}
        onPick={(example) => {
          setPrompt(example);
          void generate(example);
        }}
      />

      <div className={HELP}>
        Or skip the agent entirely — the console runs any{" "}
        <code className="font-mono text-3xs">paperclip</code> command
        directly: <code className="font-mono text-3xs">search</code>,{" "}
        <code className="font-mono text-3xs">grep</code>,{" "}
        <code className="font-mono text-3xs">cat</code>,{" "}
        <code className="font-mono text-3xs">ls</code>,{" "}
        <code className="font-mono text-3xs">map</code>, and friends. Use ↑/↓
        for history, <code className="font-mono text-3xs">clear</code> to
        reset.
      </div>
    </div>
  );
};
